import type { AlignTypes } from '@etg/wings';
import { breakpoints, Heading, Inline, Stack, vars } from '@etg/wings';
import { date } from '@eti/utils';
import { useDateFormat, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { OrderState } from '@eti/schema-types';
import { useOrderDetails } from '../../../../containers/OrderDetailsContext';
import { screenReadersOnlyStyles } from '../../../../../../utils/styleUtils';

const canceledStyles = (isCanceled: boolean) => css`
  text-decoration: ${isCanceled ? 'line-through' : 'none'};
`;

const originalTimeStyles = css`
  color: #737373;
  text-decoration: line-through;
`;

const stackStyles = css`
  width: 90px;

  @media (min-width: ${breakpoints._768}) {
    width: 105px;
  }
`;

const timeStyles = (isTimeChanged: boolean) => css`
  background-color: ${isTimeChanged ? vars.colors.signal.caution.tint : 'none'};
  position: relative;
`;

interface TimeProps {
  alignment?: AlignTypes;
  boundIndex: number;
  'data-testid': string;
  dateOfTrip: string;
  durationInDays?: number;
  locationType: 'Departure' | 'Arrival';
  originalDateOfTrip?: string;
  originalDurationInDays?: number | null;
}

const Time = ({
  alignment,
  boundIndex,
  'data-testid': dataTestId,
  dateOfTrip,
  durationInDays,
  locationType,
  originalDateOfTrip,
  originalDurationInDays,
}: TimeProps) => {
  const { t } = useTranslation();
  const { time } = useDateFormat();
  const { orderState } = useOrderDetails();
  const timeToDisplay = date.format(dateOfTrip, time);
  const originalTime = originalDateOfTrip ? date.format(originalDateOfTrip, time) : null;
  const isTimeChanged = originalTime && timeToDisplay !== originalTime;
  const isCanceled = orderState === OrderState.Canceled;
  const canceledTimeText = isTimeChanged
    ? t(`ScreenReader.ItineraryButton.${locationType}.NewTime.Canceled.Text`, timeToDisplay)
    : t(`ScreenReader.ItineraryButton.${locationType}.Time.Canceled.Text`, timeToDisplay);
  const nonCanceledTimeText = isTimeChanged
    ? t(`ScreenReader.ItineraryButton.${locationType}.NewTime.Text`, timeToDisplay)
    : t(`ScreenReader.ItineraryButton.${locationType}.Time.Text`, timeToDisplay);
  const canceledOldTimeText = t(
    `ScreenReader.ItineraryButton.${locationType}.OldTime.Canceled.Text`,
    originalTime,
  );
  const nonCanceledOldTimeText = t(
    `ScreenReader.ItineraryButton.${locationType}.OldTime.Text`,
    originalTime,
  );

  return (
    <Stack align={alignment} className={stackStyles} spacing={4}>
      <Inline
        align={alignment}
        className={cx(timeStyles(isTimeChanged), canceledStyles(isCanceled))}
        data-testid={dataTestId}
        spacing={4}
      >
        <span
          className={screenReadersOnlyStyles}
          id={`bound-${boundIndex}-${locationType.toLowerCase()}-new-time`}
        >
          {`${isCanceled ? canceledTimeText : nonCanceledTimeText} ${
            durationInDays && durationInDays > 0
              ? t('ScreenReader.ItineraryButton.ArrivalInDays.Text', durationInDays)
              : ''
          }`}
        </span>
        <Heading as="span" level={1}>
          {timeToDisplay}
        </Heading>
        {durationInDays && durationInDays > 0 ? (
          <Heading as="span" level={4}>
            +{durationInDays}
          </Heading>
        ) : null}
      </Inline>
      {isTimeChanged && (
        <Inline className={originalTimeStyles} data-testid={`${dataTestId}-original`} spacing={4}>
          <span
            className={screenReadersOnlyStyles}
            id={`bound-${boundIndex}-${locationType.toLowerCase()}-old-time`}
          >
            {`${isCanceled ? canceledOldTimeText : nonCanceledOldTimeText} ${
              originalDurationInDays && originalDurationInDays > 0
                ? t('ScreenReader.ItineraryButton.ArrivalInDays.Text', durationInDays)
                : ''
            }`}
          </span>
          <Heading as="span" level={3}>
            {originalTime}
          </Heading>
          {originalDurationInDays && originalDurationInDays > 0 ? (
            <Heading as="span" level={4}>
              +{originalDurationInDays}
            </Heading>
          ) : null}
        </Inline>
      )}
    </Stack>
  );
};
export default Time;
